/**
 * A startup's lifecycle stage.
 *
 * The stage ramp runs green → navy across the seven active stages, so position
 * in the funnel is readable at a glance. The **name is always rendered** —
 * PRD §9.7 is explicit that status is never carried by colour alone, and this
 * palette in particular is indefensible without it: seven points along one hue
 * ramp are not distinguishable to many readers, and are identical in
 * greyscale.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.16, PRD §5.3, §9.7
 */
import { useTranslation } from 'react-i18next';

import { cn } from '../../lib/cn';

import { STAGE_DOT, type Stage } from './stages';

export interface StageBadgeProps {
  stage: Stage;
  className?: string;
}

export function StageBadge({ stage, className }: StageBadgeProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <span
      className={cn(
        'bg-gray-100 text-navy-text inline-flex items-center gap-[6px] rounded-full',
        'font-display px-[11px] py-1 text-[10px] leading-[1.5] font-extrabold',
        'tracking-[0.1em] whitespace-nowrap uppercase',
        className,
      )}
    >
      {/* Decorative: the label beside it already names the stage. */}
      <span
        aria-hidden="true"
        className={cn('size-[7px] rounded-full', STAGE_DOT[stage])}
      />
      {t(`stage.${stage}`)}
    </span>
  );
}
